
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-front-purple text-white hover:bg-front-purple-dark shadow-sm hover:shadow-md",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-front-purple text-front-purple bg-background hover:bg-front-purple hover:text-white shadow-sm",
        secondary: "bg-front-gray-100 text-front-gray-900 hover:bg-front-gray-200 shadow-sm",
        ghost: "hover:bg-front-gray-100 hover:text-front-gray-900",
        link: "text-front-purple underline-offset-4 hover:underline font-medium",
        
        // Front.com inspired variants
        lime: "bg-front-lime text-front-gray-900 hover:bg-front-lime-600 shadow-sm hover:shadow-md",
        purple: "bg-front-purple text-white hover:bg-front-purple-dark shadow-sm hover:shadow-md",
        
        // Legacy support for existing code
        primary: "bg-front-purple text-white hover:bg-front-purple-dark shadow-lg hover:shadow-xl transition-all",
        accent: "bg-front-lime text-front-gray-900 hover:bg-front-lime-600 transition-all",
        minimal: "bg-white text-front-gray-900 border border-front-gray-200 hover:bg-front-gray-50 transition-all",
        gradient: "bg-gradient-to-r from-front-purple to-front-purple-dark text-white hover:opacity-90 shadow-lg hover:shadow-xl transition-all",
        tertiary: "bg-front-gray-100 text-front-gray-800 hover:bg-front-gray-200",
        blue: "bg-front-purple text-white hover:bg-front-purple-dark",
        lavender: "bg-front-gray-50 text-front-gray-900 hover:bg-front-gray-100",
        gold: "bg-front-lime text-front-gray-900 hover:bg-front-lime-600",
        sagewater: "bg-front-lime text-front-gray-900 hover:bg-front-lime-600",
        evergreen: "bg-front-purple text-white hover:bg-front-purple-dark",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
